import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import {
    StepTrailBreadcrumbs,
    type TrailStep,
} from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import React from "react";
import { Loader } from "@design-system/Loader/Loader";
import { CoaImporterSpreadsheet } from "./CoaImporterSpreadsheet";
import { CoaAccountRow, CoaImportStep, CoaValidationErrors } from "./types";
import styles from "./CoaAccountMapping.module.scss";

const STEPS: ReadonlyArray<TrailStep<CoaImportStep>> = [
    { id: CoaImportStep.UploadDocument, label: "Upload" },
    { id: CoaImportStep.EnterAccounts, label: "Review" },
    { id: CoaImportStep.MapAccounts, label: "Map" },
];

interface Props {
    step: CoaImportStep;
    entityName?: string;
    accounts: CoaAccountRow[];
    onAccountsChange: (accounts: CoaAccountRow[]) => void;
    validationErrors: CoaValidationErrors;
    hasAttemptedSubmit: boolean;
    isExtracting: boolean;
    isCreating: boolean;
    canUpload: boolean;
    onDocumentSelect: (documentId: number) => void;
    onStepChange: (step: CoaImportStep) => void;
    footerErrorMessage?: string;
}

export const CoaImporterModalContent: React.FC<Props> = ({
    step,
    entityName,
    accounts,
    onAccountsChange,
    validationErrors,
    hasAttemptedSubmit,
    isExtracting,
    isCreating,
    canUpload,
    onDocumentSelect,
    onStepChange,
    footerErrorMessage,
}) => {
    const header = (
        <StandardModalHeader className={styles.headerWithBreadcrumbs}>
            <span>Import chart of accounts</span>
            <StepTrailBreadcrumbs
                steps={STEPS}
                currentStep={step}
                onStepChange={onStepChange}
            />
        </StandardModalHeader>
    );

    if (step === CoaImportStep.UploadDocument) {
        const renderUploadControl = () => {
            if (!canUpload) {
                return (
                    <p className={styles.uploadStepDescription}>
                        Select a single entity to import accounts.
                    </p>
                );
            }
            if (isExtracting) {
                return (
                    <div className={styles.extractingState}>
                        <Loader inline />
                        <span>Processing...</span>
                    </div>
                );
            }
            return (
                <AddDocument
                    attachDocument={onDocumentSelect}
                    attachedDocumentIds={[]}
                />
            );
        };

        return (
            <>
                {header}
                <StandardModalBody className={styles.uploadStepModalBody}>
                    <div className={styles.uploadStep}>
                        <h2 className={styles.uploadStepTitle}>
                            Import your chart of accounts
                        </h2>
                        <p className={styles.uploadStepDescription}>
                            Upload a chart of accounts export
                            {entityName ? ` for ${entityName}` : ""}. We'll
                            extract the accounts so you can review and map them
                            to Kick accounts.
                        </p>
                        {renderUploadControl()}
                    </div>
                </StandardModalBody>
            </>
        );
    }

    return (
        <>
            {header}
            <StandardModalBody className={styles.modalBody}>
                <div className={styles.editAccountsSection}>
                    <p className={styles.sectionDescription}>
                        Review the extracted accounts and their classification
                        before mapping them to Kick.
                    </p>
                </div>
                <CoaImporterSpreadsheet
                    accounts={accounts}
                    onAccountsChange={onAccountsChange}
                    accountErrors={validationErrors.accounts}
                    hasAttemptedSubmit={hasAttemptedSubmit}
                />
                <CommandIsland sticky className={styles.commandIsland}>
                    {footerErrorMessage && (
                        <p className={styles.footerError}>
                            {footerErrorMessage}
                        </p>
                    )}
                    <div className={styles.commandIslandSpacer} />
                    <Button
                        variant="secondary"
                        size="md"
                        disabled={isCreating}
                        onClick={() =>
                            onStepChange(CoaImportStep.UploadDocument)
                        }
                    >
                        Back
                    </Button>
                    <WithHotkey
                        hotKey="mod+Enter"
                        hotKeyDisplayPosition="inline"
                        hotKeyOptions={{ description: "Continue" }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() =>
                                    onStepChange(CoaImportStep.MapAccounts)
                                }
                                disabled={isCreating}
                                rightIcon={hotkeyHint}
                            >
                                Continue
                            </Button>
                        )}
                    </WithHotkey>
                </CommandIsland>
            </StandardModalBody>
        </>
    );
};
